import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, FileSignature, Banknote, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const FLAT_FEE = 85.0;

export default function NewClaimPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sa, setSa] = useState(undefined); // undefined=loading, null=not signed, obj=signed
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.get("/service-agreements/me")
      .then(({ data }) => setSa(data))
      .catch(() => setSa(null));
  }, []);

  if (sa === undefined) {
    return <AppShell><div className="text-[#4A5568]">Loading…</div></AppShell>;
  }

  if (!sa) {
    return (
      <AppShell>
        <div className="mb-6">
          <Link to="/collections" className="inline-flex items-center text-sm text-[#4A5568] hover:text-[#0A192F]" data-testid="back-to-collections">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to collections
          </Link>
        </div>
        <ServiceAgreementForm onSigned={(v) => setSa(v)} user={user} />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-6">
        <Link to="/collections" className="inline-flex items-center text-sm text-[#4A5568] hover:text-[#0A192F]" data-testid="back-to-collections">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to collections
        </Link>
      </div>
      <ClaimForm user={user} sa={sa} busy={busy} setBusy={setBusy} onCreated={(c) => nav(`/collections/${c.id}`)} />
    </AppShell>
  );
}

/* ---------------- Service Agreement Form ---------------- */
function ServiceAgreementForm({ onSigned, user }) {
  const [form, setForm] = useState({
    signature_text: "", title: "",
    address: "", city: "", state: "", zip: "", phone: "", ntn_sub: "",
    credit_bureau_authorized: false,
    litigation_authorized: false,
    ach_authorized: false,
    accept_terms: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.accept_terms) { toast.error("Please accept the agreement terms"); return; }
    if (!form.signature_text) { toast.error("Type your full name to sign"); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/service-agreements", {
        signature_text: form.signature_text,
        title: form.title || null,
        ntn_sub: form.ntn_sub || null,
        address: form.address, city: form.city, state: form.state, zip: form.zip,
        phone: form.phone,
        credit_bureau_authorized: form.credit_bureau_authorized,
        litigation_authorized: form.litigation_authorized,
        ach_authorized: form.ach_authorized,
      });
      toast.success("Service Agreement signed");
      onSigned(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not sign");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-8" data-testid="service-agreement-form">
      <div className="flex items-center gap-2 mb-2">
        <FileSignature className="h-5 w-5 text-[#0A192F]" />
        <div className="font-overline text-[#4A5568]">First-time setup · Required</div>
      </div>
      <h2 className="font-serif text-3xl text-[#0A192F]">Sign the Service Agreement</h2>
      <p className="text-sm text-[#4A5568] mt-2 max-w-2xl">
        Before your first claim, please sign NTN's Flat Fee Collections Service Agreement.
        It authorizes our collection partner (NCSPlus) to act on your behalf and lets you
        set credit-bureau and litigation preferences. You'll only do this once.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
        <Field label="Authorized signer">
          <Input value={user?.name} disabled className="h-11 rounded-sm bg-[#F3EFEA]/40" />
        </Field>
        <Field label="Title (optional)">
          <Input value={form.title} onChange={(e) => set("title", e.target.value)}
            placeholder="Owner, Property Manager…" className="h-11 rounded-sm" data-testid="sa-title" />
        </Field>
        <Field label="Mailing address">
          <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="h-11 rounded-sm" data-testid="sa-address" />
        </Field>
        <Field label="NTN Sub# (optional)">
          <Input value={form.ntn_sub} onChange={(e) => set("ntn_sub", e.target.value)} className="h-11 rounded-sm" data-testid="sa-ntn-sub" />
        </Field>
        <div className="grid grid-cols-3 gap-3 md:col-span-2">
          <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} className="h-11 rounded-sm" data-testid="sa-city" /></Field>
          <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase().slice(0,2))} className="h-11 rounded-sm" placeholder="CA" data-testid="sa-state" /></Field>
          <Field label="ZIP"><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} className="h-11 rounded-sm" data-testid="sa-zip" /></Field>
        </div>
        <Field label="Phone">
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-11 rounded-sm" placeholder="555-555-1212" data-testid="sa-phone" />
        </Field>
      </div>

      {/* Authorizations */}
      <div className="mt-7 space-y-4 border-t border-[#0A192F]/10 pt-6">
        <AuthRow label="Credit Bureau Reporting"
          desc="Authorize NTN and its collection partner to report unpaid accounts to Experian, Equifax, and TransUnion."
          value={form.credit_bureau_authorized} onChange={(v) => set("credit_bureau_authorized", v)}
          required testid="sa-credit-bureau" />
        <AuthRow label="Litigation"
          desc="Authorize our partner to sue accounts when warranted. You must approve litigation on any specific account before suit is filed."
          value={form.litigation_authorized} onChange={(v) => set("litigation_authorized", v)}
          testid="sa-litigation" />
        <AuthRow label="ACH for fees (optional)"
          desc="Authorize NCSPlus to ACH your account for any outstanding invoices."
          value={form.ach_authorized} onChange={(v) => set("ach_authorized", v)}
          testid="sa-ach" />
      </div>

      {/* Terms summary */}
      <div className="mt-7 bg-[#F3EFEA]/50 border border-[#0A192F]/10 rounded-sm p-5 text-xs text-[#4A5568] leading-relaxed" data-testid="sa-terms">
        <div className="font-overline text-[#0A192F] mb-2">Terms summary</div>
        <ul className="space-y-1.5 list-disc list-inside">
          <li><strong>Flat fee:</strong> $85.00 per claim. Subscriber retains 100% of monies collected.</li>
          <li>NCSPlus will provide the Cash Recovery System with five FDCPA-compliant debtor contacts per account.</li>
          <li>Unpaid accounts may be reported to the three national credit reporting bureaus when authorized above.</li>
          <li>You agree to promptly report all debtor payments, settlements, errors, deceased, and bankrupt accounts.</li>
          <li>Governed by the laws of the State of New York. Exclusive jurisdiction: New York County, NY.</li>
          <li>You will receive the full Service Agreement PDF after signing for your records.</li>
        </ul>
      </div>

      {/* Sign */}
      <div className="mt-6 border-t border-[#0A192F]/10 pt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Type your full name to sign">
          <Input value={form.signature_text} onChange={(e) => set("signature_text", e.target.value)}
            placeholder={user?.name}
            className="h-11 rounded-sm font-serif text-lg" data-testid="sa-signature" />
        </Field>
        <div className="flex items-end">
          <label className="flex items-start gap-2 cursor-pointer text-sm text-[#0A192F]" data-testid="sa-accept">
            <Checkbox checked={form.accept_terms} onCheckedChange={(v) => set("accept_terms", !!v)} className="mt-0.5" />
            <span>I am authorized to bind the subscriber identified above and I agree to the terms of this Service Agreement.</span>
          </label>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={submit} disabled={busy} className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-12 px-6" data-testid="sa-submit">
          <ShieldCheck className="h-4 w-4 mr-2" /> {busy ? "Signing…" : "Sign Service Agreement"}
        </Button>
      </div>
    </div>
  );
}

function AuthRow({ label, desc, value, onChange, required, testid }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-sm border border-[#0A192F]/10">
      <Checkbox checked={value} onCheckedChange={(v) => onChange(!!v)} className="mt-1" data-testid={testid} />
      <div>
        <div className="font-semibold text-sm text-[#0A192F]">
          {label} {required && <span className="text-[10px] font-mono text-[#EF4444] ml-1">REQUIRED</span>}
        </div>
        <div className="text-xs text-[#4A5568] mt-0.5 leading-relaxed">{desc}</div>
        <div className="text-[10px] font-mono tracking-[0.14em] uppercase mt-2 text-[#4A5568]">
          {value ? <span className="text-[#15803D]">✓ INITIALED YES</span> : <span>○ NOT INITIALED</span>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Claim Form ---------------- */
function ClaimForm({ user, sa, busy, setBusy, onCreated }) {
  const [form, setForm] = useState({
    // Subscriber (prefilled from SA / user)
    subscriber_company_name: user?.company || "",
    subscriber_contact_name: user?.name || "",
    subscriber_ntn_sub: sa?.ntn_sub || "",
    subscriber_address: sa?.address || "",
    subscriber_city: sa?.city || "",
    subscriber_state: sa?.state || "",
    subscriber_zip: sa?.zip || "",
    subscriber_phone: sa?.phone || "",
    subscriber_email: user?.email || "",
    // Debtor
    debtor: {
      name: "", ssn_last4: "", dob: "", last_known_address: "",
      city: "", state: "", zip: "", phone1: "", phone2: "",
    },
    claim_date: new Date().toISOString().slice(0, 10),
    total_amt_owed: "",
    reason_for_collection: "",
    credit_bureau_authorization: sa?.credit_bureau_authorized ?? false,
    signature_text: "",
    accept_cert: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDebtor = (k, v) => setForm((f) => ({ ...f, debtor: { ...f.debtor, [k]: v } }));

  const submit = async () => {
    if (!form.accept_cert) { toast.error("Please confirm the certification statement"); return; }
    if (!form.signature_text) { toast.error("Type your name to sign"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        total_amt_owed: parseFloat(form.total_amt_owed),
        debtor: { ...form.debtor },
      };
      delete payload.accept_cert;
      // strip empty optional debtor fields
      ["ssn_last4", "dob", "phone2"].forEach((k) => { if (!payload.debtor[k]) delete payload.debtor[k]; });
      const { data } = await apiClient.post("/claims", payload);
      toast.success("Claim submitted — our partner will begin collection efforts");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit claim");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="font-overline text-[#4A5568] mb-2">Flat Fee Collections</div>
        <h1 className="font-serif text-4xl text-[#0A192F]" data-testid="new-claim-title">New Collection Claim</h1>
        <div className="text-sm text-[#4A5568] mt-2 inline-flex items-center gap-2">
          <Banknote className="h-4 w-4" /> Flat fee <strong className="text-[#0A192F]">$85.00</strong> · You keep 100% of recoveries
        </div>
      </div>

      {/* Subscriber */}
      <Section title="Subscriber information" testid="section-subscriber">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company name *"><Input value={form.subscriber_company_name} onChange={(e) => set("subscriber_company_name", e.target.value)} className="h-11 rounded-sm" data-testid="sub-company" /></Field>
          <Field label="Contact name *"><Input value={form.subscriber_contact_name} onChange={(e) => set("subscriber_contact_name", e.target.value)} className="h-11 rounded-sm" data-testid="sub-contact" /></Field>
          <Field label="NTN Sub# (optional)"><Input value={form.subscriber_ntn_sub} onChange={(e) => set("subscriber_ntn_sub", e.target.value)} className="h-11 rounded-sm" data-testid="sub-ntn" /></Field>
          <Field label="Phone *"><Input value={form.subscriber_phone} onChange={(e) => set("subscriber_phone", e.target.value)} className="h-11 rounded-sm" data-testid="sub-phone" /></Field>
          <Field label="Address *" full><Input value={form.subscriber_address} onChange={(e) => set("subscriber_address", e.target.value)} className="h-11 rounded-sm" data-testid="sub-address" /></Field>
          <div className="grid grid-cols-3 gap-3 md:col-span-2">
            <Field label="City *"><Input value={form.subscriber_city} onChange={(e) => set("subscriber_city", e.target.value)} className="h-11 rounded-sm" data-testid="sub-city" /></Field>
            <Field label="State *"><Input value={form.subscriber_state} onChange={(e) => set("subscriber_state", e.target.value.toUpperCase().slice(0,2))} className="h-11 rounded-sm" data-testid="sub-state" /></Field>
            <Field label="ZIP *"><Input value={form.subscriber_zip} onChange={(e) => set("subscriber_zip", e.target.value)} className="h-11 rounded-sm" data-testid="sub-zip" /></Field>
          </div>
          <Field label="Email" full><Input type="email" value={form.subscriber_email} onChange={(e) => set("subscriber_email", e.target.value)} className="h-11 rounded-sm" data-testid="sub-email" /></Field>
        </div>
      </Section>

      {/* Debtor */}
      <Section title="Debtor information" testid="section-debtor">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name *" full><Input value={form.debtor.name} onChange={(e) => setDebtor("name", e.target.value)} className="h-11 rounded-sm" data-testid="debtor-name" /></Field>
          <Field label="SSN — last 4 digits (optional)"><Input value={form.debtor.ssn_last4} onChange={(e) => setDebtor("ssn_last4", e.target.value.replace(/\D/g, "").slice(0,4))} placeholder="1234" className="h-11 rounded-sm font-mono" data-testid="debtor-ssn" /></Field>
          <Field label="Date of birth (optional)"><Input type="date" value={form.debtor.dob} onChange={(e) => setDebtor("dob", e.target.value)} className="h-11 rounded-sm" data-testid="debtor-dob" /></Field>
          <Field label="Last known address *" full><Input value={form.debtor.last_known_address} onChange={(e) => setDebtor("last_known_address", e.target.value)} className="h-11 rounded-sm" data-testid="debtor-address" /></Field>
          <div className="grid grid-cols-3 gap-3 md:col-span-2">
            <Field label="City *"><Input value={form.debtor.city} onChange={(e) => setDebtor("city", e.target.value)} className="h-11 rounded-sm" data-testid="debtor-city" /></Field>
            <Field label="State *"><Input value={form.debtor.state} onChange={(e) => setDebtor("state", e.target.value.toUpperCase().slice(0,2))} className="h-11 rounded-sm" data-testid="debtor-state" /></Field>
            <Field label="ZIP *"><Input value={form.debtor.zip} onChange={(e) => setDebtor("zip", e.target.value)} className="h-11 rounded-sm" data-testid="debtor-zip" /></Field>
          </div>
          <Field label="Phone 1 *"><Input value={form.debtor.phone1} onChange={(e) => setDebtor("phone1", e.target.value)} className="h-11 rounded-sm" data-testid="debtor-phone1" /></Field>
          <Field label="Phone 2 (optional)"><Input value={form.debtor.phone2} onChange={(e) => setDebtor("phone2", e.target.value)} className="h-11 rounded-sm" data-testid="debtor-phone2" /></Field>
        </div>
      </Section>

      {/* Claim */}
      <Section title="Claim details" testid="section-claim">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Claim date *"><Input type="date" value={form.claim_date} onChange={(e) => set("claim_date", e.target.value)} className="h-11 rounded-sm" data-testid="claim-date" /></Field>
          <Field label="Total amount owed (USD) *">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]">$</span>
              <Input type="number" step="0.01" min="0" value={form.total_amt_owed} onChange={(e) => set("total_amt_owed", e.target.value)}
                className="h-11 rounded-sm pl-7" placeholder="2500.00" data-testid="claim-amount" />
            </div>
          </Field>
          <Field label="Reason for collection *" full>
            <Textarea rows={3} value={form.reason_for_collection} onChange={(e) => set("reason_for_collection", e.target.value)}
              placeholder="Unpaid rent, damages, lease break, utilities, etc."
              className="rounded-sm" data-testid="claim-reason" />
          </Field>
        </div>
      </Section>

      {/* Certification */}
      <Section title="Certification" testid="section-cert">
        <AuthRow
          label="Authorize credit-bureau reporting on this claim"
          desc="Allows NTN and our collection partner(s) to report this unpaid account to Experian, Equifax, and TransUnion if the debtor remains unresponsive."
          value={form.credit_bureau_authorization}
          onChange={(v) => set("credit_bureau_authorization", v)}
          required testid="claim-credit-bureau" />

        <div className="bg-[#F3EFEA]/50 border border-[#0A192F]/10 rounded-sm p-5 text-xs text-[#4A5568] leading-relaxed mt-4" data-testid="claim-cert-text">
          <em>I certify that the amount indicated above is a debt owed to me by this debtor. I agree to
          notify NCSPlus or NTN of any and all payments, adjustments, and/or disputes. Failure to do so
          is a violation of the Fair Credit Reporting Act. I agree to provide necessary documentation
          of this debt if asked to do so. Further, I understand that placing this debt for collection
          is not a guarantee of receiving any money from the debtor.</em>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Field label="Type your full name to sign">
            <Input value={form.signature_text} onChange={(e) => set("signature_text", e.target.value)}
              placeholder={user?.name}
              className="h-11 rounded-sm font-serif text-lg" data-testid="claim-signature" />
          </Field>
          <div className="flex items-end">
            <label className="flex items-start gap-2 cursor-pointer text-sm text-[#0A192F]" data-testid="claim-accept">
              <Checkbox checked={form.accept_cert} onCheckedChange={(v) => set("accept_cert", !!v)} className="mt-0.5" />
              <span>I accept the certification statement above and the $85.00 flat fee for this claim.</span>
            </label>
          </div>
        </div>
      </Section>

      <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm p-4 text-xs text-[#92400E] flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Submitting this claim incurs a flat fee of <strong>${FLAT_FEE.toFixed(2)}</strong>. Placement of a debt does not guarantee recovery.</span>
      </div>

      <div className="flex justify-end gap-3">
        <Link to="/collections"><Button variant="outline" className="rounded-sm h-12 px-5">Cancel</Button></Link>
        <Button onClick={submit} disabled={busy} className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-12 px-6" data-testid="claim-submit">
          {busy ? "Submitting…" : "Submit claim"}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6 sm:p-8" data-testid={testid}>
      <h2 className="font-serif text-2xl text-[#0A192F] mb-5">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
